import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, unref as _unref, createBlock as _createBlock, renderSlot as _renderSlot, mergeProps as _mergeProps, createElementVNode as _createElementVNode, createVNode as _createVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["for"]
const _hoisted_2 = {
  key: 0,
  class: "ui-input__required"
}
const _hoisted_3 = { class: "ui-input__container" }
const _hoisted_4 = {
  key: 0,
  class: "ui-input__leading"
}
const _hoisted_5 = ["src"]
const _hoisted_6 = ["id", "type", "value", "placeholder", "disabled", "readonly", "required"]
const _hoisted_7 = {
  key: 1,
  class: "ui-input__trailing"
}
const _hoisted_8 = ["src"]
const _hoisted_9 = {
  key: 1,
  class: "ui-input__helper"
}
const _hoisted_10 = {
  key: 0,
  class: "ui-input__error"
}
const _hoisted_11 = {
  key: 1,
  class: "ui-input__helper-text"
}

import { Icon } from "@iconify/vue";
import { computed, ref, useSlots } from "vue";
import type { Size } from "./types";

interface Props {
  modelValue?: string | number;
  label?: string;
  type?:
    | "text"
    | "email"
    | "password"
    | "number"
    | "tel"
    | "url"
    | "search"
    | "date"
    | "time"
    | "datetime-local";
  placeholder?: string;
  size?: Size;
  disabled?: boolean;
  readonly?: boolean;
  required?: boolean;
  error?: string;
  helperText?: string;
  leadingIcon?: string;
  leadingIconSrc?: string;
  trailingIcon?: string;
  trailingIconSrc?: string;
  clearable?: boolean;
  id?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'Input',
  props: {
    modelValue: {},
    label: {},
    type: { default: "text" },
    placeholder: {},
    size: { default: "md" },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    error: {},
    helperText: {},
    leadingIcon: {},
    leadingIconSrc: {},
    trailingIcon: {},
    trailingIconSrc: {},
    clearable: { type: Boolean, default: false },
    id: {}
  },
  emits: ["update:modelValue", "blur", "focus"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const slots = useSlots();

const props = __props;

const emit = __emit;

const isFocused = ref(false);
const inputId = computed(() => props.id || `input-${Math.random().toString(36).substr(2, 9)}`);

const wrapperClasses = computed(() => {
  const classes = ["ui-input"];
  classes.push(`ui-input--${props.size}`);
  if (props.disabled) classes.push("ui-input--disabled");
  if (props.error) classes.push("ui-input--error");
  return classes;
});

const inputClasses = computed(() => {
  const classes = ["ui-input__field"];
  if (props.leadingIcon || props.leadingIconSrc || slots.leading) {
    classes.push("ui-input__field--has-leading");
  }
  if (props.trailingIcon || props.trailingIconSrc || slots.trailing || props.clearable) {
    classes.push("ui-input__field--has-trailing");
  }
  if (isFocused.value) classes.push("ui-input__field--focused");
  return classes;
});

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  emit("update:modelValue", target.value);
};

const handleBlur = (event: FocusEvent) => {
  isFocused.value = false;
  emit("blur", event);
};

const handleFocus = (event: FocusEvent) => {
  isFocused.value = true;
  emit("focus", event);
};

const handleClear = () => {
  emit("update:modelValue", "");
};

const inputRef = ref<HTMLInputElement | null>(null);

__expose({
  focus: () => inputRef.value?.focus(),
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(wrapperClasses.value)
  }, [
    (__props.label)
      ? (_openBlock(), _createElementBlock("label", {
          key: 0,
          for: inputId.value,
          class: "ui-input__label"
        }, [
          _createTextVNode(_toDisplayString(__props.label) + " ", 1),
          (__props.required)
            ? (_openBlock(), _createElementBlock("span", _hoisted_2, "*"))
            : _createCommentVNode("", true)
        ], 8, _hoisted_1))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_3, [
      (_unref(slots).leading || __props.leadingIcon || __props.leadingIconSrc)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            (__props.leadingIconSrc)
              ? (_openBlock(), _createElementBlock("img", {
                  key: 0,
                  src: __props.leadingIconSrc,
                  class: "ui-input__icon-img"
                }, null, 8, _hoisted_5))
              : (__props.leadingIcon)
                ? (_openBlock(), _createBlock(_unref(Icon), {
                    key: 1,
                    icon: __props.leadingIcon,
                    class: "ui-input__icon"
                  }, null, 8, ["icon"]))
                : _renderSlot(_ctx.$slots, "leading", { key: 2 })
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("input", _mergeProps({
        id: inputId.value,
        ref_key: "inputRef",
        ref: inputRef,
        type: __props.type,
        value: __props.modelValue,
        placeholder: __props.placeholder,
        disabled: __props.disabled,
        readonly: __props.readonly,
        required: __props.required,
        class: inputClasses.value
      }, _ctx.$attrs, {
        onInput: handleInput,
        onBlur: handleBlur,
        onFocus: handleFocus
      }), null, 16, _hoisted_6),
      (_unref(slots).trailing || __props.trailingIcon || __props.trailingIconSrc || __props.clearable)
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
            (__props.clearable && __props.modelValue)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  type: "button",
                  class: "ui-input__clear",
                  onClick: handleClear,
                  tabindex: "-1"
                }, [
                  _createVNode(_unref(Icon), { icon: "mdi:close" })
                ]))
              : (__props.trailingIconSrc)
                ? (_openBlock(), _createElementBlock("img", {
                    key: 1,
                    src: __props.trailingIconSrc,
                    class: "ui-input__icon-img"
                  }, null, 8, _hoisted_8))
                : (__props.trailingIcon)
                  ? (_openBlock(), _createBlock(_unref(Icon), {
                      key: 2,
                      icon: __props.trailingIcon,
                      class: "ui-input__icon"
                    }, null, 8, ["icon"]))
                  : _renderSlot(_ctx.$slots, "trailing", { key: 3 })
          ]))
        : _createCommentVNode("", true)
    ]),
    (__props.error || __props.helperText)
      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
          (__props.error)
            ? (_openBlock(), _createElementBlock("span", _hoisted_10, _toDisplayString(__props.error), 1))
            : (__props.helperText)
              ? (_openBlock(), _createElementBlock("span", _hoisted_11, _toDisplayString(__props.helperText), 1))
              : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})